import React, {useState} from 'react';
import {Circle} from '@uiw/react-color';
import {
    ExternalLinkIcon,
    LinkIcon,
    PaperclipIcon,
    PenLineIcon
} from 'lucide-react';
import CanvasController from './CanvasController.ts';
import {ResourceCardColors} from './resourceCard.ts';
import AddLinkModal from './AddLinkModal.tsx';
import {ToolButton, Toolbar, ToolbarDivider} from '../ds/ToolFrame.tsx';

interface ResourceCardToolbarProps {
    controlsDisabled: boolean;
    controller: CanvasController;
}

/*
 * Contextual controls for the selected resource card. "Open" matters more
 * than it looks: a single click only activates a card in read-only mode, so
 * without this an author has no way to check their own link while editing.
 */
const ResourceCardToolbar: React.FC<ResourceCardToolbarProps> = ({
    controlsDisabled,
    controller
}) => {
    const [showChangeLinkModal, setShowChangeLinkModal] = useState(false);

    const card = controller.activeResourceCard;
    const isFile = card?.kind === 'file';

    const onLinkEntered = (url: string, title: string) => {
        controller.setResourceCardLink(url, title || undefined);
        setShowChangeLinkModal(false);
    };

    return (
        <>
            {showChangeLinkModal && (
                <AddLinkModal
                    heading="Change link"
                    confirmLabel="Save"
                    initialUrl={card?.kind === 'link' ? card.url : ''}
                    initialTitle={card?.title ?? ''}
                    onClose={() => setShowChangeLinkModal(false)}
                    onLinkEntered={onLinkEntered}
                />
            )}
            <Toolbar label="Resource card" className="whiteboard-toolbar">
                <ToolButton
                    label="Edit label"
                    icon={PenLineIcon}
                    disabled={controlsDisabled}
                    onClick={() => controller.editResourceCardTitle()}
                />
                <ToolButton
                    label={isFile ? 'Change file' : 'Change link'}
                    icon={isFile ? PaperclipIcon : LinkIcon}
                    disabled={controlsDisabled}
                    onClick={() =>
                        isFile
                            ? controller.replaceResourceCardFile()
                            : setShowChangeLinkModal(true)
                    }
                />
                <ToolButton
                    label={isFile ? 'Download file' : 'Open link'}
                    icon={ExternalLinkIcon}
                    disabled={controlsDisabled}
                    onClick={() => controller.openActiveResourceCard()}
                />
                <ToolbarDivider />
                <div className="mx-toolbar-swatches" title="Card colour">
                    <Circle
                        colors={ResourceCardColors}
                        color={card?.color}
                        rectProps={{
                            style: {width: 18, height: 18, borderRadius: '50%'}
                        }}
                        style={{flexWrap: 'nowrap', gap: 2}}
                        onChange={color => {
                            if (!controlsDisabled) {
                                controller.setResourceCardColor(color.hex);
                            }
                        }}
                    />
                </div>
            </Toolbar>
        </>
    );
};

export default ResourceCardToolbar;
